<template>
  <div>
    <b-card no-body>
      <div class="m-2">
        <b-row class="align-items-center">
          <b-col cols="auto">
            <b-button variant="flat-secondary" @click="$router.push({ name: 'app-chat-suporte' })">
              <feather-icon icon="ArrowLeftIcon" class="mr-50" />
              Voltar
            </b-button>
          </b-col>
          <b-col>
            <div v-if="conversation">
              <h5 class="mb-0">{{ conversation.nome_cliente || 'Cliente' }}</h5>
              <small class="text-muted">{{ conversation.email_cliente || '' }}</small>
            </div>
          </b-col>
          <b-col cols="auto" v-if="conversation">
            <b-badge pill :variant="statusVariant(conversation.status)" class="mr-1">
              {{ statusLabel(conversation.status) }}
            </b-badge>
            <b-button
              v-if="conversation.status === 'aguardando'"
              size="sm"
              class="mr-1"
              :style="{ backgroundColor: '#F26522', borderColor: '#F26522' }"
              variant="primary"
              @click="atenderConversa"
            >
              Atender
            </b-button>
            <b-form-select
              v-model="newStatus"
              :options="statusChangeOptions"
              size="sm"
              style="width: 180px; display: inline-block;"
              @change="changeStatus"
            />
          </b-col>
        </b-row>

        <hr />

        <div v-if="conversation" class="mb-1">
          <b-badge pill :variant="conversation.tipo === 'vendedor' ? 'info' : 'secondary'" class="mr-1">
            {{ conversation.tipo === 'vendedor' ? 'Chat Vendedor' : 'Suporte' }}
          </b-badge>
          <span v-if="conversation.id_pedido" class="mr-2"><strong>Pedido:</strong> #{{ conversation.id_pedido }}</span>
          <strong>Assunto:</strong> {{ conversation.assunto || '-' }}
          <span class="ml-2"><strong>Categoria:</strong> {{ conversation.categoria || '-' }}</span>
        </div>

        <div ref="messageContainer" class="chat-messages-container" style="max-height: 500px; overflow-y: auto; padding: 1rem; background: #f8f8f8; border-radius: 6px;">
          <div v-if="loadingMessages" class="text-center py-3">
            <b-spinner small />
          </div>
          <div v-for="msg in messages" :key="msg.id" :class="messageClass(msg)" class="mb-2">
            <div :class="bubbleClass(msg)" class="chat-bubble p-1 px-2" style="max-width: 70%; border-radius: 12px; display: inline-block;">
              <div class="font-weight-bold small mb-25">{{ senderLabel(msg) }}</div>
              <div>{{ msg.mensagem }}</div>
              <div class="text-muted small mt-25 text-right">{{ formatTime(msg.data_inclusao) }}</div>
            </div>
          </div>
          <div v-if="!loadingMessages && messages.length === 0" class="text-center text-muted py-3">
            Nenhuma mensagem ainda.
          </div>
        </div>

        <hr />

        <b-row class="align-items-end">
          <b-col>
            <b-form-textarea
              v-model="replyText"
              placeholder="Digite sua resposta..."
              rows="3"
              no-resize
              @keydown.enter.ctrl="sendReply"
            />
          </b-col>
          <b-col cols="auto">
            <b-button
              :style="{ backgroundColor: '#F26522', borderColor: '#F26522' }"
              variant="primary"
              :disabled="!replyText.trim() || sending"
              @click="sendReply"
            >
              <feather-icon icon="SendIcon" class="mr-50" />
              Enviar
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BBadge, BFormTextarea, BFormSelect, BSpinner,
} from 'bootstrap-vue'
import { useApi } from '@/composables/useApi'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BFormTextarea,
    BFormSelect,
    BSpinner,
  },
  data() {
    return {
      conversation: null,
      messages: [],
      replyText: '',
      sending: false,
      loadingMessages: false,
      newStatus: '',
      pollingInterval: null,
      statusChangeOptions: [
        { value: '', text: 'Alterar Status' },
        { value: 'em_atendimento', text: 'Em Atendimento' },
        { value: 'resolvido', text: 'Resolvido' },
        { value: 'fechado', text: 'Fechado' },
      ],
    }
  },
  computed: {
    chatId() {
      return this.$route.params.id
    },
  },
  mounted() {
    this.fetchConversation()
    this.fetchMessages()
    this.pollingInterval = setInterval(() => {
      this.fetchMessages(true)
    }, 5000)
  },
  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }
  },
  methods: {
    async fetchConversation() {
      try {
        const api = useApi()
        const { data } = await api.get(`/chat/${this.chatId}`)
        this.conversation = data
        this.newStatus = ''
      } catch (err) {
        console.error('Erro ao buscar conversa:', err)
      }
    },
    async fetchMessages(silent = false) {
      if (!silent) this.loadingMessages = true
      try {
        const api = useApi()
        const { data } = await api.get(`/chat/${this.chatId}/mensagens`)
        this.messages = Array.isArray(data) ? data : (data.data || [])
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      } catch (err) {
        console.error('Erro ao buscar mensagens:', err)
      } finally {
        this.loadingMessages = false
      }
    },
    async sendReply() {
      if (!this.replyText.trim() || this.sending) return
      this.sending = true
      try {
        const api = useApi()
        const isVendor = this.conversation && this.conversation.tipo === 'vendedor'
        const endpoint = isVendor ? `/chat/${this.chatId}/vendor-reply` : `/chat/${this.chatId}/mensagens`
        await api.post(endpoint, {
          mensagem: this.replyText,
          remetente_tipo: isVendor ? 'vendedor' : 'admin',
        })
        this.replyText = ''
        await this.fetchMessages()
      } catch (err) {
        console.error('Erro ao enviar mensagem:', err)
      } finally {
        this.sending = false
      }
    },
    async atenderConversa() {
      try {
        const api = useApi()
        await api.put(`/chat/${this.chatId}/atender`)
        await this.fetchConversation()
      } catch (err) {
        console.error('Erro ao atender conversa:', err)
      }
    },
    async changeStatus(status) {
      if (!status) return
      try {
        const api = useApi()
        await api.put(`/chat/${this.chatId}/status`, { status })
        await this.fetchConversation()
      } catch (err) {
        console.error('Erro ao alterar status:', err)
      }
    },
    scrollToBottom() {
      const container = this.$refs.messageContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },
    messageClass(msg) {
      if (msg.remetente_tipo === 'admin' || msg.remetente_tipo === 'vendedor') return 'd-flex justify-content-end'
      if (msg.remetente_tipo === 'bot') return 'd-flex justify-content-center'
      return 'd-flex justify-content-start'
    },
    bubbleClass(msg) {
      if (msg.remetente_tipo === 'admin' || msg.remetente_tipo === 'vendedor') return 'bg-primary text-white'
      if (msg.remetente_tipo === 'bot') return 'bg-light text-dark'
      return 'bg-white border'
    },
    senderLabel(msg) {
      if (msg.remetente_tipo === 'admin') return 'Admin'
      if (msg.remetente_tipo === 'vendedor') return 'Vendedor'
      if (msg.remetente_tipo === 'bot') return 'Bot'
      return msg.remetente_nome || 'Cliente'
    },
    statusVariant(status) {
      const map = {
        aguardando: 'warning',
        em_atendimento: 'info',
        resolvido: 'success',
        fechado: 'secondary',
      }
      return map[status] || 'primary'
    },
    statusLabel(status) {
      const map = {
        aguardando: 'Aguardando',
        em_atendimento: 'Em Atendimento',
        resolvido: 'Resolvido',
        fechado: 'Fechado',
      }
      return map[status] || status
    },
    formatTime(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.chat-bubble {
  word-break: break-word;
}
</style>
